import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import TrashIcon from '../../../../components/icons/TrashIcon';
import CheckoutCartItemPriceDisplay from './CheckoutCartItemPriceDisplay';
import CartItemVendor from './CartItemVendor';
import { getAsset } from '../../../../util/assets';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import { getDigitalBundleCartItemPrice } from '../../redux/helpers';
import { getIdentityName } from '../../../../util/identity';
import Button from '../../../../components/global/Button';
import PhotoSelectionOverlayModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/DigitalBundleOption/components/PhotoSelectionOverlayModal';
import SaveIcon from '../../../../components/icons/SaveIcon';
import CartItemPhotoPreview from './CartItemPhotoPreview';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.LEGACY_PRINT]).isRequired,
    digitalBundle: PropTypes.shape({
      id: PropTypes.string.isRequired,
      photos_count: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }).isRequired,
    }).isRequired,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        thumbnail_urls: PropTypes.arrayOf(
          PropTypes.shape({
            url: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
            size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE))
              .isRequired,
          }).isRequired,
        ).isRequired,
      }).isRequired,
    ),
    quantity: PropTypes.number.isRequired,
  }).isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.object,
    discount_price: PropTypes.object,
  }),
  loading: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const DigitalBundleCartItem = ({
  item,
  pricing,
  loading,
  onUpdate,
  onRemove,
  watermarkSettings,
}) => {
  const [photoSelectorOpen, setPhotoSelectorOpen] = useState(false);

  const { digitalBundle, identity, albumMembershipGroup, photos } = item;

  const isSinglePhotoBundle = digitalBundle?.photos_count === 1;
  const price = getDigitalBundleCartItemPrice(item);
  const identityName = getIdentityName(identity);

  const descriptionCopy = (() => {
    if (!photos?.length) {
      return '';
    }

    if (isSinglePhotoBundle) {
      return 'Unlock access to hi-res digital downloads for this photo!';
    }

    if (digitalBundle.is_multi_identity) {
      return `${pluralize('Download', photos.length, true)} selected`;
    }

    return `${pluralize(
      'Download',
      photos.length,
      true,
    )} selected for ${identityName}`;
  })();

  return (
    <div className="checkout-cart-item checkout-digital-bundle-cart-item">
      <div className="checkout-cart-item-content">
        <div className="checkout-cart-item-figure">
          {isSinglePhotoBundle ? (
            <CartItemPhotoPreview
              photo={photos[0]}
              watermarkSettings={watermarkSettings}
            >
              <span className="checkout-cart-item-photo-icon">
                <SaveIcon color="#0d84fe" size={18} />
              </span>
            </CartItemPhotoPreview>
          ) : (
            <Fragment>
              <div className="purchasing-option-item-thumb">
                <img
                  src={getAsset('/icon-photo-pass.svg')}
                  width="62px"
                  alt=""
                />
              </div>
              <IdentityAvatar identity={identity} />
            </Fragment>
          )}
        </div>

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title">
            {isSinglePhotoBundle
              ? 'Individual Download'
              : `Digital Bundle: ${pluralize(
                  'photo',
                  digitalBundle.photos_count,
                  true,
                )}`}
            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </span>

          <CheckoutCartItemPriceDisplay
            clientPrice={price}
            pricing={pricing}
            loading={loading}
          />
          <CartItemVendor cartItem={item} />

          <div className="checkout-cart-item-description">
            {descriptionCopy}
          </div>
          {(photos ?? []).length > 0 && !isSinglePhotoBundle && (
            <Button
              btnSize="xs"
              btnStyle="link"
              className="text-extra-bold"
              onClick={() => setPhotoSelectorOpen(true)}
            >
              Update photo selection
            </Button>
          )}
        </div>
      </div>
      <PhotoSelectionOverlayModal
        forceOpen={photoSelectorOpen}
        onClose={() => setPhotoSelectorOpen(false)}
        maxPhotos={digitalBundle.photos_count}
        albumMembershipGroupId={albumMembershipGroup?.id}
        identityId={digitalBundle.is_multi_identity ? undefined : identity?.id}
        currentSelectedPhotos={photos}
        onComplete={({ photos: newPhotos }) =>
          onUpdate(item.id, { photos: newPhotos })
        }
        watermarkSettings={watermarkSettings}
      />
    </div>
  );
};

DigitalBundleCartItem.propTypes = propTypes;

export default DigitalBundleCartItem;
